import underlineSvg from '../../../assets/images/icons/underline.svg'
import type { CSSProperties } from 'react'
import type { SectionTheme } from '../../theme/sectionTheme'

type TestimonialCardProps = {
  logo: string
  description: string
  star?: string
  name: string
  roleAtCompany?: string
  program?: string
  ctc?: string
  emoji?: string
  isCenter?: boolean
  side?: 'left' | 'right'
  theme?: SectionTheme
}

const TestimonialCard = ({
  logo,
  description,
  star,
  name,
  roleAtCompany,
  program,
  ctc,
  emoji,
  isCenter = false,
  side,
  theme = 'default',
}: TestimonialCardProps) => {
  const isUpskillCard = Boolean(roleAtCompany || program || ctc)
  const themeStyles: Record<
    SectionTheme,
    {
      centerBorder: string
      sideBorder: string
      logoTintStyle?: CSSProperties
      cardBg: string
      cardInnerClass: string
      showGlow: boolean
      topGlow: string
      bottomGlow: string
      underlineBg: string
    }
  > = {
    default: {
      centerBorder: 'p-[2px] bg-testimonial-border-purple-center',
      sideBorder: 'p-[1px] bg-testimonial-border-purple-side',
      logoTintStyle: undefined,
      cardBg: 'bg-panel-dark',
      cardInnerClass: 'backdrop-blur-md shadow-lg',
      showGlow: true,
      topGlow: 'bg-purple-soft/60',
      bottomGlow: 'bg-purple-alt/35',
      underlineBg: 'bg-purple',
    },
    greentheme: {
      centerBorder: 'p-[2px] bg-testimonial-border-green-center',
      sideBorder: 'p-[1px] bg-testimonial-border-green-side',
      logoTintStyle: { filter: 'brightness(0) saturate(100%) invert(83%) sepia(31%) saturate(669%) hue-rotate(41deg) brightness(100%) contrast(95%)' },
      cardBg: 'bg-testimonial-green-bg',
      cardInnerClass: 'backdrop-blur-md shadow-lg',
      showGlow: true,
      topGlow: 'bg-testimonial-green/60',
      bottomGlow: 'bg-testimonial-green/30',
      underlineBg: 'bg-testimonial-green',
    },
    rabbitxtheme: {
      centerBorder: 'p-[2px] bg-testimonial-border-rabbitx-center',
      sideBorder: 'p-[1px] bg-testimonial-border-rabbitx-side',
      logoTintStyle: { filter: 'brightness(0) saturate(100%) invert(32%) sepia(85%) saturate(2358%) hue-rotate(341deg) brightness(94%) contrast(97%)' },
      cardBg: 'bg-card-rabbitx',
      cardInnerClass: 'backdrop-blur-md shadow-lg',
      showGlow: true,
      topGlow: 'bg-rabbitx-accent/50',
      bottomGlow: 'bg-rabbitx-secondary/30',
      underlineBg: 'bg-rabbitx-accent',
    },
    arbventuretheme: {
      centerBorder: 'p-[1px] bg-testimonial-border-arbventure-center',
      sideBorder: 'p-[1px] bg-testimonial-border-arbventure-side',
      logoTintStyle: { filter: 'brightness(0) saturate(100%) invert(55%) sepia(50%) saturate(500%) hue-rotate(60deg) brightness(95%) contrast(90%)' },
      cardBg: 'bg-testimonial-card-arbventure',
      cardInnerClass: 'backdrop-blur-md shadow-lg',
      showGlow: true,
      topGlow: 'bg-arbventure-theme-accent/12',
      bottomGlow: 'bg-arbventure-theme-accent-bright/6',
      underlineBg: 'bg-arbventure-theme-accent',
    },
    lightpinktheme: {
      centerBorder: 'p-[2px] bg-testimonial-border-tatvacaretheme-white-center',
      sideBorder: 'p-[1px] bg-testimonial-border-tatvacaretheme-white-side',
      logoTintStyle: { filter: 'brightness(0) invert(1)' },
      cardBg: 'bg-testimonialcardbg',
      cardInnerClass: 'border border-white/25',
      showGlow: false,
      topGlow: '',
      bottomGlow: '',
      underlineBg: 'bg-black',
    },
    ddxtheme: {
      centerBorder: 'p-[2px] bg-testimonial-border-ddxtheme-white-center',
      sideBorder: 'p-[1px] bg-testimonial-border-ddxtheme-white-side',
      logoTintStyle: { filter: 'brightness(0) invert(1)' },
      cardBg: 'bg-[#510c7c]',
      cardInnerClass: 'border border-white/20 backdrop-blur-md',
      showGlow: true,
      topGlow: 'bg-white/25',
      bottomGlow: 'bg-white-bright/25',
      underlineBg: 'bg-black',
    },
    medilearntheme: {
      centerBorder: 'p-[2px] bg-testimonial-border-medilearntheme-white-center',
      sideBorder: 'p-[1px] bg-testimonial-border-medilearntheme-white-side',
      logoTintStyle: { filter: 'brightness(0) invert(1)' },
      cardBg: 'bg-[#9db16dfb]', 
      cardInnerClass: 'border border-white/20 backdrop-blur-md',
      showGlow: true,
      topGlow: 'bg-white/25',
      bottomGlow: 'bg-white/25',
      underlineBg: 'bg-black',
    },
  }

  const cardPositionClass = isCenter
    ? 'md:-translate-y-10 md:rotate-0'
    : side === 'left'
      ? 'md:translate-y-6 md:-rotate-5'
      : 'md:translate-y-6 md:rotate-5'


  const selectedTheme = themeStyles[theme] ?? themeStyles.default
  const borderClass = isCenter ? selectedTheme.centerBorder : selectedTheme.sideBorder

  return (
    <div
     className={`mt-20 mb-60 transition-transform duration-300
  w-[200px] h-[200px]
  md:w-[250px] md:h-[250px]
  lg:w-[340px] lg:h-[340px]
  xl:w-[400px] xl:h-[400px]
  min-[1440px]:w-[clamp(470px,26vw,620px)] min-[1440px]:h-[clamp(470px,26vw,620px)]
  rounded-[28px] md:rounded-[32px] lg:rounded-[38px] xl:rounded-[44px] min-[1440px]:rounded-[3.5vw]
  ${cardPositionClass}
  ${borderClass}
`}
    >
      {/* INNER CARD — full square, flex column, space-between to push name to bottom */}
      <div className={`group relative overflow-hidden ${selectedTheme.cardBg} ${selectedTheme.cardInnerClass} w-full h-full flex flex-col justify-between p-4 md:p-4 lg:p-6 xl:p-7 
      min-[1440px]:p-[1.7vw] rounded-[28px] md:rounded-[32px] lg:rounded-[38px] xl:rounded-[44px] min-[1440px]:rounded-[3.5vw]
      cursor-pointer transition-all duration-300
      `}>

        {selectedTheme.showGlow && (
          <span className={`pointer-events-none absolute -top-10 -left-10 h-40 w-30 rounded-full blur-3xl ${selectedTheme.topGlow}`} />
        )}

        {/* ── TOP: logo top-left ── */}
        <img
          src={logo}
          alt={name}
          style={selectedTheme.logoTintStyle}
          className="
            relative z-10 object-contain self-start
            w-10 h-6
            md:w-10 md:h-6
            lg:w-14 lg:h-9
            xl:w-16 xl:h-10
            min-[1440px]:w-[3.8vw] min-[1440px]:h-[2.4vw]
          "
        />

        {/* ── MIDDLE: description ── */}
        <p
  className="
    relative z-10 font-gilroy-medium text-white
    text-[11px] md:text-[11px] lg:text-[15px] xl:text-[17px] min-[1440px]:text-[1.03vw]
    overflow-hidden
  "
  style={{
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 6,
    lineHeight: '1.5',
    maxHeight: '9em',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
  }}
>
  {description}
</p>

        {/* ── BOTTOM ROW: stars left  |  name right ── */}
        <div className="relative z-10 w-full">

          {/* STARS — bottom left */}
          {!isUpskillCard && (
            <div className="flex gap-0.5 lg:gap-1 min-[1440px]:gap-[0.31vw]">
              {[...Array(5)].map((_, idx) => (
                <img
                  key={idx}
                  src={star || "/staricon.svg"}
                  alt="star"
                  className=" mb-5 lg:mb-6 xl:mb-7 min-[1440px]:mb-[1.5vw]
                    w-3 h-3
                    lg:w-5 lg:h-5
                    xl:w-6 xl:h-6
                    min-[1440px]:w-[1.35vw] min-[1440px]:h-[1.35vw]
                  "
                />
              ))}
            </div>
          )}

          {/* NAME + META */}
          <div className={isUpskillCard ? 'text-left' : 'text-end'}>
            <h4 className="
              text-white font-rajdhani font-semibold relative inline-block
              text-sm
              lg:text-xl
              xl:text-2xl
              min-[1440px]:text-[1.45vw]
            ">
              <span className="relative z-10">{name}</span>
              {!isUpskillCard && (
                <>
                  <br />
                  <span
                    className={`absolute left-1/2 top-3 min-[1440px]:top-[0.95vw] z-0 h-4 min-[1440px]:h-[1.27vw] w-[140%] -translate-x-1/2 transition-colors duration-300 ${selectedTheme.underlineBg}`}
                    style={{
                      WebkitMaskImage: `url(${underlineSvg})`,
                      maskImage: `url(${underlineSvg})`,
                      WebkitMaskSize: '100% 100%',
                      maskSize: '100% 100%',
                      WebkitMaskRepeat: 'no-repeat',
                      maskRepeat: 'no-repeat',
                      WebkitMaskPosition: 'center',
                      maskPosition: 'center',
                    }}
                    aria-hidden
                  />
                </>
              )}
            </h4>
            {roleAtCompany && (
              <p className="mt-2 min-[1440px]:mt-[0.63vw] font-gilroy-semibold text-[11px] text-purple lg:text-xs xl:text-sm min-[1440px]:text-[1.1vw]">
                {roleAtCompany}
              </p>
            )}
            {program && (
              <p className="mt-1 min-[1440px]:mt-[0.31vw] font-gilroy-medium text-[10px] text-gray-300 lg:text-[11px] xl:text-xs min-[1440px]:text-[0.95vw]">
                {program}
              </p>
            )}
            {ctc && (
              <p className="mt-2 min-[1440px]:mt-[0.63vw] font-gilroy-bold text-sm text-purple lg:text-base xl:text-xl min-[1440px]:text-[1.58vw]">
                {ctc}
              </p>
            )}
          </div>

          {/* STARS after text block for Upskill cards */}
          {isUpskillCard && (
            <div className="mt-3 min-[1440px]:mt-[0.95vw] flex gap-0.5 lg:gap-1 min-[1440px]:gap-[0.31vw]">
              {[...Array(5)].map((_, idx) => (
                <img
                  key={`u-${idx}`}
                  src={star || "/staricon.svg"}
                  alt="star"
                  className="w-3 h-3 lg:w-4 lg:h-4 xl:w-5 xl:h-5 min-[1440px]:w-[1.58vw] min-[1440px]:h-[1.58vw]"
                />
              ))}
            </div>
          )}

          {/* bottom-right emoji/icon for Upskill */}
          {isUpskillCard && (
            <div className="absolute bottom-1 right-1 min-[1440px]:bottom-[0.31vw] min-[1440px]:right-[0.31vw] text-[28px] leading-none lg:text-[34px] min-[1440px]:text-[2.71vw]">
              {emoji || '👩‍💼'}
            </div>
          )}

        </div>
      </div>
    </div>
  )
}

export default TestimonialCard
